import Cookie from 'js-cookie'
import http, { setAuthorizationHeader } from './axios'
import { applyAuthSession, clearAuthSession } from './auth'

/** 从 Cookie 补全企业客户 ID / 用户名（/sso/me 写入），供未走网关 UserContext 的接口使用 */
const withUserSession = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return payload
  }
  const userId = Cookie.get('userId')
  const username = Cookie.get('username')
  return {
    ...payload,
    ...(userId && !payload.userId ? { userId } : {}),
    ...(username && !payload.username ? { username } : {})
  }
}

const withUserIdParams = (params) => {
  const userId = Cookie.get('userId')
  return userId ? { ...params, userId: params?.userId ?? userId } : params
}

const fetchLogin = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/user-service/v1/login',
    data: body,
    _skipAuthRetry: true
  })
  if (data?.data?.accessToken) {
    applyAuthSession(data.data)
    setAuthorizationHeader(data.data.accessToken)
  }
  return data
}

const fetchRegister = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/user-service/register',
    data: body
  })
  return data
}

const fetchTicketSearch = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/ticket-service/ticket/query',
    params: withUserIdParams(params)
  })
  return data
}

const fetchRegionStation = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/ticket-service/region-station/query',
    params
  })
  return data
}

const fetchPassengerList = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/user-service/passenger/query',
    params
  })
  return data
}
const fetchDeletePassenger = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/user-service/passenger/remove',
    data: body
  })
  return data
}

const fetchAddPassenger = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/user-service/passenger/save',
    data: body
  })
  return data
}

const fetchEditPassenger = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/user-service/passenger/update',
    data: body
  })
  return data
}
const fetchLogout = async (body) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/user-service/logout',
    data: body
  })
  clearAuthSession()
  setAuthorizationHeader(null)
  return data
}

const fetchBuyTicket = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/ticket-service/ticket/purchase/v2',
    data: withUserSession(body)
  })

  return data
}

const fetchOrderBySn = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/order-service/order/ticket/query',
    params
  })
  return data
}

const fetchOrderStatusLog = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/order-service/order/status-log/list',
    params
  })
  return data
}

const fetchPay = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/pay-service/pay/create',
    data: body
  })
  return data
}

const fetchWalletInfo = async () => {
  const { data } = await http({
    method: 'GET',
    url: '/api/pay-service/wallet/info'
  })
  return data
}

const fetchWalletPay = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/pay-service/pay/wallet',
    data: body
  })
  return data
}

const fetchWalletReschedulePay = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/pay-service/pay/wallet/reschedule',
    data: body
  })
  return data
}

const fetchStationAll = async () => {
  const { data } = await http({
    method: 'GET',
    url: '/api/ticket-service/station/all'
  })
  return data
}

const fechUserInfo = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/user-service/query',
    params
  })
  return data
}

const fetchTrainStation = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/ticket-service/train-station/query',
    params
  })
  return data
}

const fetchTicketList = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/order-service/order/ticket/page',
    params
  })
  return data
}

const fetchOrderCancel = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/ticket-service/ticket/cancel',
    data: body
  })
  return data
}

const fetchUserUpdate = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/user-service/update',
    data: body
  })
  return data
}

const fetchOrderStatus = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/pay-service/pay/query/order-sn',
    params
  })
  return data
}

const fetchMyTicket = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/order-service/order/ticket/self/page',
    params
  })
  return data
}

const fetchRefundTicket = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/ticket-service/ticket/refund',
    data: body
  })
  return data
}

const fetchRefundOrderList = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/pay-service/refund/page',
    params
  })
  return data
}

const fetchRefundByOrderSn = async (params) => {
  const { data } = await http({
    method: 'GET',
    url: '/api/pay-service/refund/query-by-order',
    params
  })
  return data
}

const fetchRescheduleTicket = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/ticket-service/ticket/reschedule',
    data: withUserSession(body)
  })
  return data
}

const fetchRefundFeeEstimate = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/ticket-service/ticket/refund-fee-estimate',
    data: body
  })
  return data
}

const fetchRescheduleFeeEstimate = async (body) => {
  const { data } = await http({
    method: 'POST',
    url: '/api/ticket-service/ticket/reschedule-fee-estimate',
    data: withUserSession(body)
  })
  return data
}

const fetchSpringFestivalRanges = async () => {
  const { data } = await http({
    method: 'GET',
    url: '/api/ticket-service/ticket/spring-festival-ranges'
  })
  return data
}

export {
  fetchLogin,
  fetchRegister,
  fetchTicketSearch,
  fetchRegionStation,
  fetchPassengerList,
  fetchDeletePassenger,
  fetchAddPassenger,
  fetchEditPassenger,
  fetchLogout,
  fetchBuyTicket,
  fetchOrderBySn,
  fetchOrderStatusLog,
  fetchPay,
  fetchWalletInfo,
  fetchWalletPay,
  fetchWalletReschedulePay,
  fetchStationAll,
  fechUserInfo,
  fetchTrainStation,
  fetchTicketList,
  fetchOrderCancel,
  fetchOrderStatus,
  fetchUserUpdate,
  fetchMyTicket,
  fetchRefundTicket,
  fetchRefundOrderList,
  fetchRefundByOrderSn,
  fetchRescheduleTicket,
  fetchRefundFeeEstimate,
  fetchRescheduleFeeEstimate,
  fetchSpringFestivalRanges
}
